import { api } from '../../shared/api/client';
import type { ApprovalRequest, ContentPiece, InventoryItem, TodayReading, Me, Membership, MyAccount, MyAssignment, Subscription } from '../../shared/api/types';
import { daysLabel, isLapsed } from '../billing/format';
import { load as loadDenomination } from '../denomination/denomination';

/**
 * "Qué tengo pendiente" es un AGREGADOR: no existe una entidad Task.
 * Cada elemento sale de su objeto original (Membresía, …) y deja de aparecer
 * cuando ese objeto deja de estar pendiente. Cada fuente se autoriza por separado.
 */
export interface PendingItem {
  key: string;
  title: string;
  detail: string;
  to?: string;
}

type Source = (me: Me, signal: AbortSignal) => Promise<PendingItem[]>;

/** Mis solicitudes de Membresía esperando respuesta. */
const myMembershipRequests: Source = async (_me, signal) => {
  const { items } = await api.get<{ items: (Membership & { churchName: string })[] }>('/multichurch/me/memberships', signal);
  return items
    .filter((m) => m.status === 'pending')
    .map((m) => ({
      key: `my-request-${m.id}`,
      title: `Tu solicitud a ${m.churchName || 'una iglesia'}: pendiente de aprobación`,
      detail: 'La iglesia la revisará; te avisaremos cuando te acepten.',
    }));
};

/** Solicitudes que yo debo aprobar en mi contexto activo. */
const requestsToApprove: Source = async (me, signal) => {
  const canApprove = me.permissions.some((p) => p.permission === 'members.approve' && p.scope !== 'self');
  if (!me.activeChurchId || !canApprove) return [];
  const { items } = await api.get<{ items: Membership[] }>(`/multichurch/churches/${me.activeChurchId}/memberships?status=pending`, signal);
  if (items.length === 0) return [];
  return [
    {
      key: 'requests-to-approve',
      title: items.length === 1 ? '1 solicitud de membresía' : `${items.length} solicitudes de membresía`,
      detail: 'Personas que quieren unirse a tu iglesia.',
      to: '/app/solicitudes',
    },
  ];
};

/** Mi correo sin confirmar. */
const emailVerification: Source = async (_me, signal) => {
  const account = await api.get<MyAccount>('/identity/me', signal);
  const items: PendingItem[] = [];
  if (!account.emailVerified) {
    items.push({ key: 'email-verification', title: 'Confirma tu correo', detail: `Abre el enlace que enviamos a ${account.email}.`, to: '/app/perfil' });
  }
  if (!account.gender || !account.birthDate || !account.birthCountry || !account.address || account.baptized === null) {
    items.push({ key: 'complete-details', title: 'Completa tus datos', detail: 'Tu género, fecha y lugar de nacimiento, dirección, teléfonos y si estás bautizado.', to: '/app/perfil' });
  }
  return items;
};

/** Suscripción de la Iglesia activa: avisa cuando la prueba está por terminar o ya venció. */
const subscriptionStatus: Source = async (me, signal) => {
  const canManage = me.permissions.some((p) => p.permission === 'billing.manage' && p.scope !== 'self');
  if (!me.activeChurchId || !canManage) return [];
  const { subscription: s } = await api.get<{ subscription: Subscription | null }>(`/saas/churches/${me.activeChurchId}/subscription`, signal);
  const to = '/app/facturacion';
  if (!s) return [{ key: 'subscription', title: 'Elige un plan para tu iglesia', detail: 'Empieza la prueba gratuita.', to }];
  if (s.status === 'pending') {
    return [{ key: 'subscription', title: 'Tu iglesia está pendiente de activación', detail: 'Paga tu plan para activarla al momento, o espera a que KEORA la active.', to }];
  }
  if (isLapsed(s.status)) {
    return [{ key: 'subscription', title: 'Tu iglesia está en solo lectura', detail: 'Paga la mensualidad para volver a modificar datos.', to }];
  }
  if (s.status === 'trialing' && s.daysLeft <= 5) {
    return [{ key: 'subscription', title: `La prueba termina en ${daysLabel(s.daysLeft)}`, detail: `Plan ${s.plan.name}.`, to }];
  }
  return [];
};

/** Asignaciones que esperan mi respuesta (Motor 8). Dejan de aparecer al aceptar o declinar. */
const assignmentsToConfirm: Source = async (me, signal) => {
  if (!me.activeChurchId || !me.permissions.some((p) => p.permission === 'assignments.read')) return [];
  const { items } = await api.get<{ items: MyAssignment[] }>(`/assignments/churches/${me.activeChurchId}/me/assignments`, signal);
  return items
    .filter((a) => a.status === 'requested' && a.occasion.status !== 'canceled' && new Date(a.occasion.endsAt) > new Date())
    .map((a) => ({
      key: `assignment-${a.id}`,
      title: `Te asignaron: ${a.role}`,
      detail: `${a.occasion.title}, ${new Date(a.occasion.startsAt).toLocaleString('es', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}.`,
      to: '/app/servicio',
    }));
};

/** Solicitudes que esperan una decisión mía (Motor 21). No incluye las propias: nadie decide lo suyo. */
const approvalsToDecide: Source = async (me, signal) => {
  const canDecide = me.permissions.some((p) => p.permission === 'approvals.decide' && p.scope !== 'self');
  if (!me.activeChurchId || !canDecide) return [];
  const { items } = await api.get<{ items: ApprovalRequest[] }>(`/approvals/churches/${me.activeChurchId}/requests?list=pending`, signal);
  const others = items.filter((r) => r.requestedBy.personId !== me.personId);
  if (others.length === 0) return [];
  return [
    {
      key: 'approvals-to-decide',
      title: others.length === 1 ? '1 solicitud por aprobar' : `${others.length} solicitudes por aprobar`,
      detail: others.length === 1 ? others[0]!.title : 'Inscripciones y solicitudes esperando tu decisión.',
      to: '/app/aprobaciones',
    },
  ];
};

/** Mis solicitudes que siguen esperando una decisión. */
const myPendingApprovals: Source = async (me, signal) => {
  if (!me.activeChurchId || !me.permissions.some((p) => p.permission === 'approvals.read')) return [];
  const { items } = await api.get<{ items: ApprovalRequest[] }>(`/approvals/churches/${me.activeChurchId}/requests?list=mine`, signal);
  return items
    .filter((r) => r.status === 'pending')
    .map((r) => ({ key: `my-approval-${r.id}`, title: r.title, detail: 'Esperando aprobación.', to: '/app/aprobaciones?list=mine' }));
};

/** Lecturas de hoy de mis planes (Motor 14): solo las propias. */
const todayReadings: Source = async (me, signal) => {
  if (!me.activeChurchId || !me.permissions.some((p) => p.permission === 'devotional.read')) return [];
  const { items } = await api.get<{ items: TodayReading[] }>(`/devotional/churches/${me.activeChurchId}/me/today`, signal);
  return items.map((r) => ({
    key: `reading-${r.planId}`,
    title: `Lectura de hoy: ${r.passages.join(', ')}`,
    detail: `${r.planTitle} · día ${r.day} de ${r.totalDays}`,
    to: `/app/devocional/${r.planId}`,
  }));
};

/** Contenido que espera revisión humana (Motor 12), para quien aprueba. */
const contentToReview: Source = async (me, signal) => {
  const canApprove = me.permissions.some((p) => p.permission === 'content.approve' && p.scope !== 'self');
  if (!me.activeChurchId || !canApprove) return [];
  const { items } = await api.get<{ items: ContentPiece[] }>(`/content/churches/${me.activeChurchId}/pieces?status=in_review`, signal);
  if (items.length === 0) return [];
  return [
    {
      key: 'content-review',
      title: items.length === 1 ? `Revisar: ${items[0]!.title}` : `${items.length} piezas de contenido por revisar`,
      detail: items.slice(0, 3).map((i) => i.title).join(', '),
      to: items.length === 1 ? `/app/contenido/${items[0]!.id}` : '/app/contenido?estado=in_review',
    },
  ];
};

/** Artículos por debajo de su mínimo (Motor 19), para quien administra el inventario. */
const lowStock: Source = async (me, signal) => {
  const canManage = me.permissions.some((p) => p.permission === 'inventory.manage' && p.scope !== 'self');
  if (!me.activeChurchId || !canManage) return [];
  const { items } = await api.get<{ items: InventoryItem[] }>(`/inventory/churches/${me.activeChurchId}/stock`, signal);
  const low = items.filter((i) => i.low);
  if (low.length === 0) return [];
  return [
    {
      key: 'low-stock',
      title: low.length === 1 ? `Poca existencia: ${low[0]!.name}` : `${low.length} artículos con poca existencia`,
      detail: low.slice(0, 3).map((i) => `${i.name} (${i.total} ${i.unit})`).join(', '),
      to: '/app/inventario',
    },
  ];
};

/** Visitantes a los que yo doy seguimiento. */
const myVisitors: Source = async (me, signal) => {
  if (!me.activeChurchId) return [];
  const { items } = await api.get<{ items: { id: string; givenName: string; familyName: string }[] }>(`/multichurch/churches/${me.activeChurchId}/visitors?mine=1`, signal);
  return items.map((v) => ({ key: `visitor-${v.id}`, title: `Seguimiento: ${v.givenName} ${v.familyName}`.trim(), detail: 'Visitante a tu cargo. Llámale o escríbele.', to: `/app/visitantes/${v.id}` }));
};

/** Asambleas de Dios: credenciales ministeriales vencidas o por vencer (60 días). */
const credentialsDue: Source = async (me, signal) => {
  const canManage = me.permissions.some((p) => p.permission === 'members.manage' && p.scope !== 'self');
  if (!me.activeChurchId || !canManage) return [];
  const den = await loadDenomination(me.activeChurchId);
  if (den.current?.key !== 'asambleas-de-dios' || den.current.status !== 'verified') return [];
  const { items } = await api.get<{ items: { name: string; status: string }[] }>(`/multichurch/churches/${me.activeChurchId}/ad/credentials`, signal);
  const due = items.filter((c) => c.status !== 'vigente');
  if (due.length === 0) return [];
  return [
    {
      key: 'ad-credentials-due',
      title: due.length === 1 ? '1 credencial ministerial por renovar' : `${due.length} credenciales ministeriales por renovar`,
      detail: due.map((c) => c.name).join(', '),
      to: '/app/asambleas/credenciales',
    },
  ];
};

export const PENDING_SOURCES: readonly Source[] = [
  credentialsDue,
  myVisitors,
  subscriptionStatus,
  emailVerification,
  requestsToApprove,
  approvalsToDecide,
  lowStock,
  contentToReview,
  todayReadings,
  assignmentsToConfirm,
  myMembershipRequests,
  myPendingApprovals,
];

export async function loadPending(me: Me, signal: AbortSignal, sources = PENDING_SOURCES): Promise<PendingItem[]> {
  // Una fuente que falla no oculta las demás.
  const results = await Promise.allSettled(sources.map((source) => source(me, signal)));
  return results.flatMap((r) => (r.status === 'fulfilled' ? r.value : []));
}
